import { FormHelperText } from "@mui/material";
import { NodeConfig } from "../../types/nodes";
import { WorkflowNode, InputDataRow } from "../../types/workflow";
import RunConfigField from "./RunConfigField";
import { SetStateAction, useEffect, useMemo, useState } from "react";
import InputDataSection from "./InputDataSection";
import { ResponseConfigOption } from "../../types/common";

type TriggerSetupProps = {
    openConditionsDetails: boolean,
    localConditions: any,
    removeCondition: any,
    setLocalConditions: any,
    responseConfig: any,
    isAnyStepIncomplete: boolean,
    setConditionsOpenDetails: (openDetails: boolean) => void,
    addNewCondition: () => void,
    onSave: (action?: string, cb?: () => void, isIntermittentSave?: boolean) => void,
    workflowNode: WorkflowNode,
    nodeRunConfig: Record<string, string | null>,
    setNodeRunConfigAndRefNodeId: (key: string, value: string, refNodeId?: string) => void,
    nodeConfig: NodeConfig,
    saveConfigField: (key: string) => void,
    chosenAction: string,
    resetNodeRunConfigInRef: (run_config: Record<string, string>, run_config_metadata: Record<string, {ref_node_id: string}>) => void,
    inputRows: InputDataRow[],
    setInputRows: (value: SetStateAction<InputDataRow[]>) => void,
    filterText: string,
    nodeId: string,
    countToNameMap: Record<string, string>,
    isFetchingSavedNodeConfig: boolean,
};

const getDisplayValue = (key: string, nodeRunConfig: Record<string, string | null>): string => {
    if (nodeRunConfig && key in nodeRunConfig) {
        return (nodeRunConfig as Record<string, unknown>)?.[key]?.toString().trim() || '';
    }

    return '';
};

const TriggerSetupSalesforceCreateCase = ({
    nodeRunConfig,
    setNodeRunConfigAndRefNodeId,
    nodeConfig,
    responseConfig,
    chosenAction,
    saveConfigField,
    openConditionsDetails,
    localConditions,
    removeCondition,
    setLocalConditions,
    isAnyStepIncomplete,
    setConditionsOpenDetails,
    addNewCondition,
    onSave,
    workflowNode,
    resetNodeRunConfigInRef,
    inputRows,
    setInputRows,
    filterText,
    nodeId,
    countToNameMap,
    isFetchingSavedNodeConfig,
}: TriggerSetupProps) => {
    const { run_config_metadata, run_config_render_data } = nodeConfig || {};
    const shouldRender = (nodeRunConfig && !!Object.keys(nodeRunConfig)?.length);

    const dropdownOptionIdToOptionMap = useMemo(() => {
        let dropdownOptionIdToOptionMap: Record<string, ResponseConfigOption> = {};
        if (responseConfig?.length) {
            responseConfig.forEach((option: ResponseConfigOption) => {
                dropdownOptionIdToOptionMap[`${option.id}:${option.refNodeId}`] = {
                    ...option,
                    value: typeof option.value !== "object" ? option.value : ""
                }
            });
        }

        return dropdownOptionIdToOptionMap;
    }, [responseConfig]);
    
    const filteredNodeRunConfig = useMemo(() => {
        const keysToFilter = ['endpoint', 'client_id', 'client_secret', 'data'];
        return Object.entries(nodeRunConfig || {}).reduce((acc, [key, value]) => {
            if (!keysToFilter.includes(key)) {
                acc[key] = value;
            }
            return acc;
        }, {} as Record<string, string | null>);
    }, [nodeRunConfig]);

    const filteredKeysNodeRunConfig = useMemo(() => {
        const keysToFilter = ['endpoint', 'client_id', 'client_secret'];
        return Object.entries(nodeRunConfig || {}).reduce((acc, [key, value]) => {
            if (keysToFilter.includes(key)) {
                acc[key] = value;
            }
            return acc;
        }, {} as Record<string, string | null>);
    }, [nodeRunConfig]);

    const keysToRender = useMemo(() => {
        return new Set([
            ...(filteredKeysNodeRunConfig && Object.keys(filteredKeysNodeRunConfig)?.length ? Object.keys(filteredKeysNodeRunConfig) : [])
        ]);
    }, [filteredKeysNodeRunConfig]);

    const getDisplayOrderVal = (value: number | undefined) => {
        if (typeof value === 'number') {
            return value;
        }

        return 100000;
    }

    if (!shouldRender) return <FormHelperText>No details for this action</FormHelperText>;

    return (
        <>
            {Array.from(keysToRender).sort((a, b) => getDisplayOrderVal(run_config_render_data?.[a]?.display_order) - getDisplayOrderVal(run_config_render_data?.[b]?.display_order)).map(key => (
                <RunConfigField
                    key={key}
                    keyName={key}
                    renderConfig={run_config_render_data?.[key] || { type: 'text' }}
                    refNodeId={run_config_metadata?.[key]?.ref_node_id || ''}
                    setNodeRunConfigAndRefNodeId={setNodeRunConfigAndRefNodeId}
                    responseConfig={responseConfig}
                    displayValue={getDisplayValue(key, nodeRunConfig)}
                    saveConfigField={saveConfigField}
                    openConditionsDetails={openConditionsDetails}
                    localConditions={localConditions}
                    removeCondition={removeCondition}
                    setLocalConditions={setLocalConditions}
                    isAnyStepIncomplete={isAnyStepIncomplete}
                    setConditionsOpenDetails={setConditionsOpenDetails}
                    addNewCondition={addNewCondition}
                    onSave={onSave}
                    workflowNode={workflowNode}
                    filterText={filterText}
                    countToNameMap={countToNameMap}
                    isFetchingSavedNodeConfig={isFetchingSavedNodeConfig}
                />
            ))}
            <div className="font-medium my-4">Case Data</div>
            <InputDataSection
                nodeRunConfig={filteredNodeRunConfig}
                nodeRunConfigMetadata={run_config_metadata}
                responseConfig={responseConfig}
                inputRows={inputRows}
                setInputRows={setInputRows}
                dropdownOptionIdToOptionMap={dropdownOptionIdToOptionMap}
                onSave={onSave}
                nodeId={nodeId}
                countToNameMap={countToNameMap}
            />
        </>
    );
}

export default TriggerSetupSalesforceCreateCase;
